import { useState, useEffect, useCallback } from 'react';
import { User, Lock, Edit3, ArrowRight, MapPin, Calendar, Phone, Mail, CreditCard, AlertCircle, CheckCircle, Loader2, Eye, EyeOff, Shield } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { regions } from '../../data/regions';
import { locationsByRegion } from '../../data/locationsByRegion';
import { updateProfile, logout } from '../../store/authSlice';
import authService from '../../Services/AuthService';
import './Settings.css';

export default function Settings() {
  const { user } = useSelector((state) => state.auth);
  console.log("user: ", user)
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Initialize form data with user data from Redux
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone_number || '',
    gender: user?.gender || 'Male',
    ageRange: '24-35',
    location: user?.region || '',
    subLocation: user?.location || '',
    ghanaCard: user?.card_number || '',
    ghana_card_prefix: '',
    ghana_card_middle: '',
    ghana_card_suffix: '',
    document_type: user?.document_type || '',
    license_prefix: '',
    license_middle: '',
    license_suffix: '',
    issueDate: user?.issue_date || '',
    expiryDate: user?.expiry_date || ''
  });

  const [uploadedImages, setUploadedImages] = useState({
    front: user?.id_card_front ? { preview: user.id_card_front } : null,
    back: user?.id_card_back ? { preview: user.id_card_back } : null
  });

  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState('');
  
  // Password change states
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  
  // Password visibility states
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      // Map region name to region ID
      const regionId = regions.find(r => r.name.toLowerCase() === user.region?.toLowerCase())?.id || '';
      
      // Parse existing license plate if available
      let licensePrefix = '', licenseMiddle = '', licenseSuffix = '';
      if (user.license_plate_number) {
        const parts = user.license_plate_number.split('-');
        if (parts.length === 3) {
          licensePrefix = parts[0] || '';
          licenseMiddle = parts[1] || '';
          licenseSuffix = parts[2] || '';
        }
      }

      // Parse existing Ghana Card if available
      let ghanaCardPrefix = '', ghanaCardMiddle = '', ghanaCardSuffix = '';
      if (user.card_number && user.document_type === 'ghana_card') {
        // Remove hyphens and parse Ghana Card format: GHA1234567891
        const cleanNumber = user.card_number.replace(/-/g, '');
        if (cleanNumber.length >= 13) {
          ghanaCardPrefix = cleanNumber.substring(0, 3); // GHA
          ghanaCardMiddle = cleanNumber.substring(3, 12); // 123456789
          ghanaCardSuffix = cleanNumber.substring(12, 13); // 1
        }
      }

      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone_number || '',
        gender: user.gender || 'Male',
        ageRange: calculateAgeRange(user.dob),
        location: regionId,
        subLocation: user.location || '',
        ghanaCard: user.card_number || '',
        ghana_card_prefix: ghanaCardPrefix,
        ghana_card_middle: ghanaCardMiddle,
        ghana_card_suffix: ghanaCardSuffix,
        document_type: user.document_type || '',
        license_prefix: licensePrefix,
        license_middle: licenseMiddle,
        license_suffix: licenseSuffix,
        issueDate: user.issue_date || '',
        expiryDate: user.expiry_date || ''
      });

      setUploadedImages({
        front: user.id_card_front ? { preview: user.id_card_front } : null,
        back: user.id_card_back ? { preview: user.id_card_back } : null
      });

      // Check if current location is "Other" (custom)
      const availableLocations = locationsByRegion[regionId] || [];
      if (user.location && !availableLocations.includes(user.location)) {
        setShowCustomLocation(true);
        setCustomLocation(user.location);
        // Set subLocation to "Other" to show the custom input
        setFormData(prev => ({ ...prev, subLocation: 'Other' }));
      }
    }
  }, [user]);

  const navigationItems = [
    { id: 'profile', label: 'Profile', icon: User, description: 'Personal & Contact Information' },
    { id: 'security', label: 'Security', icon: Lock, description: 'Password & Account Security' }
  ];

  // Helper function to calculate age range from date of birth
  const calculateAgeRange = (dob) => {
    if (!dob) return '24-35';
    
    const birthYear = parseInt(dob.substring(0, 4));
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear;
    
    if (age >= 18 && age <= 23) return '18-23';
    if (age >= 24 && age <= 35) return '24-35';
    if (age >= 36 && age <= 45) return '36-45';
    if (age >= 46 && age <= 55) return '46-55';
    return '55+';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Ghana Card handler functions (matching AgentSignUp)
  const handleGhanaCardPrefixChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    handleInputChange('ghana_card_prefix', value);
  };

  const handleGhanaCardMiddleChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
    handleInputChange('ghana_card_middle', value);
  };

  const handleGhanaCardSuffixChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    handleInputChange('ghana_card_suffix', value);
  };

  // Ghana Card validation (matching AgentSignUp)
  const isValidGhanaCard = () => {
    const prefix = formData.ghana_card_prefix || '';
    const middle = formData.ghana_card_middle || '';
    const suffix = formData.ghana_card_suffix || '';
    
    return prefix.length === 3 && middle.length === 9 && suffix.length === 1;
  };

  const handleRegionChange = (regionId) => {
    setFormData(prev => ({ ...prev, location: regionId }));
    setShowCustomLocation(false);
    setCustomLocation('');
    setFormData(prev => ({ ...prev, subLocation: '' }));
  };

  const handleLocationChange = (location) => {
    if (location === 'Other') {
      setShowCustomLocation(true);
      setFormData(prev => ({ ...prev, subLocation: 'Other' }));
    } else {
      setShowCustomLocation(false);
      setCustomLocation('');
      setFormData(prev => ({ ...prev, subLocation: location }));
    }
  };

  const handleCustomLocationChange = (value) => {
    setCustomLocation(value);
    setFormData(prev => ({ ...prev, subLocation: value }));
  };

  const getAvailableLocations = () => {
    const regionId = formData.location;
    const locations = locationsByRegion[regionId] || [];
    
    // If user has a custom location, add "Other" option
    if (showCustomLocation && customLocation) {
      return [...locations, 'Other'];
    }
    
    return locations;
  };

  // Get full Ghana Card number for API (without hyphens) - matching AgentSignUp
  const getFullGhanaCardNumber = () => {
    const prefix = formData.ghana_card_prefix || '';
    const middle = formData.ghana_card_middle || '';
    const suffix = formData.ghana_card_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    // Combine without hyphens for API payload
    return prefix + middle + suffix;
  };

  // Get full license plate for API - matching AgentSignUp
  const getFullLicensePlate = () => {
    const prefix = formData.license_prefix || '';
    const middle = formData.license_middle || '';
    const suffix = formData.license_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    let fullPlate = prefix;
    if (middle) fullPlate += '-' + middle;
    if (suffix) fullPlate += '-' + suffix;
    
    return fullPlate;
  };

  // Check if user is a rider
  const isRider = user?.agent_type === 'rider' || user?.agent_type === 'driver';

  const handleImageUpload = (type, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages(prev => ({
          ...prev,
          [type]: {
            file: file,
            preview: e.target.result
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (type) => {
    setUploadedImages(prev => ({
      ...prev,
      [type]: null
    }));
  };

  const handleSave = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // Convert files to base64 if they exist (matching AgentSignUp structure)
      const convertFileToBase64 = async (file) => {
        if (!file) return null;
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      const idCardFrontBase64 = uploadedImages.front?.file ? await convertFileToBase64(uploadedImages.front.file) : null;
      const idCardBackBase64 = uploadedImages.back?.file ? await convertFileToBase64(uploadedImages.back.file) : null;

      // Prepare the payload for the API (matching AgentSignUp structure)
      const selectedRegion = regions.find(r => r.id === formData.location);
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        gender: formData.gender,
        region: selectedRegion?.name || '',
        location: formData.subLocation === 'Other' ? customLocation : formData.subLocation,
        // Note: email and phone_number are not included as they cannot be changed
      };

      // Add optional fields only if they have values
      const fullLicensePlate = getFullLicensePlate();
      if (fullLicensePlate && isRider) {
        payload.license_plate_number = fullLicensePlate;
      }
      
      if (formData.document_type) {
        payload.document_type = formData.document_type;
      }
      
      // Use appropriate card number based on document type
      if (formData.document_type === 'ghana_card') {
        const ghanaCardNumber = getFullGhanaCardNumber();
        if (ghanaCardNumber) {
          payload.card_number = ghanaCardNumber;
        }
      } else if (formData.ghanaCard) {
        payload.card_number = formData.ghanaCard;
      }
      
      if (formData.issueDate) {
        payload.issue_date = formData.issueDate;
      }
      if (formData.expiryDate) {
        payload.expiry_date = formData.expiryDate;
      }

      // Add image uploads to payload
      if (idCardFrontBase64) {
        payload.id_card_front = idCardFrontBase64;
      }
      if (idCardBackBase64) {
        payload.id_card_back = idCardBackBase64;
      }

      // Call the API
      const response = await authService.updateProfile(payload);

      if (response.status) {
        dispatch(updateProfile({
          first_name: formData.firstName,
          last_name: formData.lastName,
          gender: formData.gender,
          region: selectedRegion?.name || '',
          location: formData.subLocation === 'Other' ? customLocation : formData.subLocation,
          document_type: formData.document_type,
          card_number: formData.document_type === 'ghana_card' ? getFullGhanaCardNumber() : formData.ghanaCard,
          license_plate_number: fullLicensePlate,
          issue_date: formData.issueDate,
          expiry_date: formData.expiryDate,
          id_card_front: idCardFrontBase64 || user?.id_card_front,
          id_card_back: idCardBackBase64 || user?.id_card_back,
        }));

        toast.success(response.message || 'Profile updated successfully!');
        setIsEditing(false);
      } else {
        toast.error(response.message || 'Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = useCallback((field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  }, []);

  const togglePasswordVisibility = useCallback((field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const calculatePasswordStrength = useCallback((password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    let feedback = [];
    
    // Length check
    if (password.length >= 8) score += 1;
    else feedback.push('At least 8 characters');
    
    // Lowercase check
    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Lowercase letter');
    
    // Uppercase check
    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Uppercase letter');
    
    // Number check
    if (/\d/.test(password)) score += 1;
    else feedback.push('Number');
    
    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    else feedback.push('Special character');
    
    // Length bonus
    if (password.length >= 12) score += 1;
    
    const strengthLevels = [
      { score: 0, label: 'Very Weak', color: 'bg-red-500' },
      { score: 1, label: 'Weak', color: 'bg-red-400' },
      { score: 2, label: 'Fair', color: 'bg-orange-400' },
      { score: 3, label: 'Good', color: 'bg-yellow-400' },
      { score: 4, label: 'Strong', color: 'bg-green-400' },
      { score: 5, label: 'Very Strong', color: 'bg-green-500' },
      { score: 6, label: 'Excellent', color: 'bg-green-600' }
    ];
    
    const level = strengthLevels[Math.min(score, 6)];
    
    return {
      score,
      label: level.label,
      color: level.color,
      feedback: feedback.length > 0 ? feedback : []
    };
  }, []);

  // Reusable password input component
  const PasswordInput = useCallback(({ 
    label, 
    value, 
    onChange, 
    placeholder, 
    disabled, 
    showPassword, 
    onToggleVisibility,
    showStrength = false 
  }) => {
    const strength = showStrength ? calculatePasswordStrength(value) : null;
    
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="px-4 py-3 pr-12 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
          <button
            type="button"
            onClick={onToggleVisibility}
            disabled={disabled}
            className="absolute right-3 top-1/2 text-gray-400 transform -translate-y-1/2 hover:text-gray-600 disabled:text-gray-300"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        
        {/* Password Strength Indicator */}
        {showStrength && value && (
          <div className="space-y-2">
            <div className="flex gap-2 items-center">
              <Shield size={14} className="text-gray-500" />
              <span className="text-xs text-gray-600">Password Strength:</span>
              <span className={`text-xs font-medium ${
                strength.score <= 2 ? 'text-red-600' : 
                strength.score <= 4 ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {strength.label}
              </span>
            </div>
            
            {/* Strength Bar */}
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6].map((level) => (
                <div
                  key={level}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    level <= strength.score 
                      ? strength.color 
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            {/* Feedback */}
            {strength.feedback.length > 0 && (
              <div className="text-xs text-gray-600">
                <p className="mb-1 font-medium">Add:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {strength.feedback.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [calculatePasswordStrength]);

  const validatePasswordForm = () => {
    if (!passwordData.oldPassword) {
      toast.error('Please enter your current password');
      return false;
    }
    if (!passwordData.newPassword) {
      toast.error('Please enter a new password');
      return false;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return false;
    }
    if (passwordData.oldPassword === passwordData.newPassword) {
      toast.error('New password must be different from current password');
      return false;
    }
    return true;
  };

  const handleChangePassword = async () => {
    if (isChangingPassword) return;
    
    if (!validatePasswordForm()) return;
    
    setIsChangingPassword(true);
    
    try {
      const payload = {
        old_password: passwordData.oldPassword,
        password: passwordData.newPassword,
        password_confirmation: passwordData.confirmPassword
      };

      const response = await authService.changePassword(payload);

      if (response.status) {
        toast.success(response.message || 'Password changed successfully!');
        
        // Clear password fields
        setPasswordData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordFields(false);
        
        // Logout user and redirect to login page
        setTimeout(() => {
          dispatch(logout());
          toast.info('Please log in again with your new password');
          window.location.href = '/signin';
        }, 2000);
      } else {
        toast.error(response.message || 'Failed to change password. Please try again.');
      }
    } catch (error) {
      console.error('Password change error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const renderProfileSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Personal & Contact Information</h2>
          {user && (
            <div className="flex gap-4 mt-2 text-sm">
              <span className={`flex items-center gap-1 ${
                user.email_verified_at ? 'text-green-600' : 'text-orange-600'
              }`}>
                <Mail size={14} />
                Email {user.email_verified_at ? 'Verified' : 'Unverified'}
              </span>
              <span className={`flex items-center gap-1 ${
                user.phone_number_verified_at ? 'text-green-600' : 'text-orange-600'
              }`}>
                <Phone size={14} />
                Phone {user.phone_number_verified_at ? 'Verified' : 'Unverified'}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          disabled={isUpdating}
          className={`flex gap-2 items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            isUpdating
              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
              : isEditing
              ? 'text-red-600 bg-red-50 hover:bg-red-100'
              : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
          }`}
        >
          <Edit3 size={16} />
          {isUpdating ? 'Updating...' : isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Personal Details */}
        <div className="space-y-4">
          <h3 className="pb-2 text-lg font-medium text-gray-900 border-b border-gray-200">Personal Details</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                    disabled={!isEditing || isUpdating}
                className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={!isEditing || isUpdating}
                className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Email Address</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="email"
                  value={formData.email}
                  disabled={true}
                  className="py-3 pr-4 pl-10 w-full text-gray-500 bg-gray-50 rounded-lg border border-gray-300 cursor-not-allowed"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle size={18} className="text-orange-500" />
                </div>
              </div>
              <div className="p-3 mt-2 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex gap-2 items-start">
                  <AlertCircle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-800">Email cannot be changed</p>
                    <p className="mt-1 text-orange-700">
                      To change your email address, please contact our support team for assistance.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Phone Number</label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="tel"
                  value={formData.phone}
                  disabled={true}
                  className="py-3 pr-4 pl-10 w-full text-gray-500 bg-gray-50 rounded-lg border border-gray-300 cursor-not-allowed"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle size={18} className="text-orange-500" />
                </div>
              </div>
              <div className="p-3 mt-2 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex gap-2 items-start">
                  <AlertCircle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-800">Phone number cannot be changed</p>
                    <p className="mt-1 text-orange-700">
                      To change your phone number, please contact our support team for assistance.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  disabled={!isEditing || isUpdating}
                  className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Age Range</label>
                <select
                  value={formData.ageRange}
                  onChange={(e) => handleInputChange('ageRange', e.target.value)}
                  disabled={!isEditing || isUpdating}
                  className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="18-23">18-23</option>
                  <option value="24-35">24-35</option>
                  <option value="36-45">36-45</option>
                  <option value="46-55">46-55</option>
                  <option value="55+">55+</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Region</label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <select
                  value={formData.location}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  disabled={!isEditing || isUpdating}
                  className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select Region</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Location</label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <select
                  value={formData.subLocation}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  disabled={!isEditing || !formData.location}
                  className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select Location</option>
                  {getAvailableLocations().map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Custom Location Input */}
              {showCustomLocation && (
                <div className="mt-3">
                  <label className="block mb-2 text-sm font-medium text-gray-700">Custom Location</label>
                  <div className="relative">
                    <MapPin size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="text"
                      value={customLocation}
                      onChange={(e) => handleCustomLocationChange(e.target.value)}
                      placeholder="Enter your custom location"
                      disabled={!isEditing || isUpdating}
                      className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Document Upload */}
        <div className="space-y-4">
          <h3 className="pb-2 text-lg font-medium text-gray-900 border-b border-gray-200">Document Upload</h3>
          
          <div className="space-y-4">
            <div className="space-y-4">
              <div className="w-full">
                <label className="block mb-2 text-sm font-medium text-gray-700">GHANA CARD FRONT</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('front', e.target.files[0])}
                    disabled={!isEditing || isUpdating}
                    className="hidden"
                    id="front-upload"
                  />
                  <label
                    htmlFor="front-upload"
                    className={`block w-full h-48 rounded-lg border-2 border-emerald-300 border-dashed transition-colors ${
                      !isEditing || isUpdating
                        ? 'bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'bg-emerald-50 cursor-pointer hover:bg-emerald-100'}`}
                  >
                    {uploadedImages.front ? (
                      <div className="relative flex justify-center items-center w-full h-full">
                        <img
                          src={uploadedImages.front.preview}
                          alt="Ghana Card Front"
                          className="object-contain max-h-full max-w-full rounded-lg"
                        />
                        {isEditing && !isUpdating && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              removeImage('front');
                            }}
                            className="flex absolute top-2 right-2 justify-center items-center w-6 h-6 text-white bg-red-500 rounded-full transition-colors hover:bg-red-600"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col justify-center items-center w-full h-full">
                        <CreditCard size={32} className="mb-2 text-emerald-600" />
                        <p className="text-xs text-emerald-600">Click to upload front image</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              
              <div className="w-full">
                <label className="block mb-2 text-sm font-medium text-gray-700">GHANA CARD BACK</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('back', e.target.files[0])}
                    disabled={!isEditing || isUpdating}
                    className="hidden"
                    id="back-upload"
                  />
                  <label
                    htmlFor="back-upload"
                    className={`block w-full h-48 rounded-lg border-2 border-emerald-300 border-dashed transition-colors ${
                      !isEditing || isUpdating
                        ? 'bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'bg-emerald-50 cursor-pointer hover:bg-emerald-100'}`}
                  >
                    {uploadedImages.back ? (
                      <div className="relative flex justify-center items-center w-full h-full">
                        <img
                          src={uploadedImages.back.preview}
                          alt="Ghana Card Back"
                          className="object-contain max-h-full max-w-full rounded-lg"
                        />
                        {isEditing && !isUpdating && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              removeImage('back');
                            }}
                            className="flex absolute top-2 right-2 justify-center items-center w-6 h-6 text-white bg-red-500 rounded-full transition-colors hover:bg-red-600"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col justify-center items-center w-full h-full">
                        <CreditCard size={32} className="mb-2 text-emerald-600" />
                        <p className="text-xs text-emerald-600">Click to upload back image</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Document Type</label>
              <select
                value={formData.document_type}
                onChange={(e) => handleInputChange('document_type', e.target.value)}
                disabled={!isEditing || isUpdating}
                className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select Document Type</option>
                <option value="ghana_card">Ghana Card</option>
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's License</option>
              </select>
            </div>

            {/* Conditional Card Number Input */}
            {formData.document_type === 'ghana_card' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Ghana Card Number</label>
                <div className="flex gap-2 items-center">
                  {/* Prefix Input (3 letters) */}
                  <input
                    type="text"
                    placeholder="GHA"
                    value={formData.ghana_card_prefix || ''}
                    onChange={handleGhanaCardPrefixChange}
                    maxLength={3}
                    disabled={!isEditing || isUpdating}
                    className="px-3 py-3 w-20 text-sm text-center uppercase rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <span className="text-gray-400">-</span>
                  
                  {/* Middle Input (9 digits) */}
                  <input
                    type="text"
                    placeholder="123456789"
                    value={formData.ghana_card_middle || ''}
                    onChange={handleGhanaCardMiddleChange}
                    maxLength={9}
                    disabled={!isEditing || isUpdating}
                    className="px-3 py-3 w-32 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <span className="text-gray-400">-</span>
                  
                  {/* Suffix Input (1 digit) */}
                  <input
                    type="text"
                    placeholder="1"
                    value={formData.ghana_card_suffix || ''}
                    onChange={handleGhanaCardSuffixChange}
                    maxLength={1}
                    disabled={!isEditing || isUpdating}
                    className="px-3 py-3 w-12 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                
                {/* Validation Feedback */}
                {(formData.ghana_card_prefix || formData.ghana_card_middle || formData.ghana_card_suffix) && (
                  <div className="space-y-1">
                    {!isValidGhanaCard() && (
                      <p className="text-xs text-red-500">
                        Please complete all fields: 3 letters, 9 digits, 1 digit
                      </p>
                    )}
                    {isValidGhanaCard() && (
                      <p className="text-xs text-green-600">
                        ✓ Ghana Card format is valid
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Card Number</label>
                <input
                  type="text"
                  value={formData.ghanaCard}
                  onChange={(e) => handleInputChange('ghanaCard', e.target.value)}
                  disabled={!isEditing || isUpdating}
                  className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Issue Date</label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) => handleInputChange('issueDate', e.target.value)}
                    disabled={!isEditing || isUpdating}
                    className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Expiry Date</label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    disabled={!isEditing || isUpdating}
                    className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* License Plate (for riders only) */}
        {isRider && (
          <div className="space-y-4">
            <h3 className="pb-2 text-lg font-medium text-gray-900 border-b border-gray-200">License Plate</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">License Plate Number</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={formData.license_prefix}
                    onChange={(e) => handleInputChange('license_prefix', e.target.value.toUpperCase())}
                    placeholder="GR"
                    maxLength={2}
                    disabled={!isEditing || isUpdating}
                    className="px-3 py-3 w-16 text-center rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="text"
                    value={formData.license_middle}
                    onChange={(e) => handleInputChange('license_middle', e.target.value.replace(/\D/g, ''))}
                    placeholder="1234"
                    maxLength={4}
                    disabled={!isEditing || isUpdating}
                    className="px-3 py-3 w-20 text-center rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="text"
                    value={formData.license_suffix}
                    onChange={(e) => handleInputChange('license_suffix', e.target.value.replace(/\D/g, ''))}
                    placeholder="21"
                    maxLength={2}
                    disabled={!isEditing || isUpdating}
                    className="px-3 py-3 w-16 text-center rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Format: GR-1234-21</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={isUpdating}
            className={`flex items-center gap-2 px-6 py-3 font-medium text-white rounded-lg transition-all duration-200 ${
              isUpdating
                ? 'bg-emerald-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 hover:shadow-lg'
            }`}
          >
            {isUpdating ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Updating Profile...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );


  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Security</h2>
          <p className="mt-1 text-sm text-gray-600">Manage your password and account security</p>
        </div>
        <button 
          onClick={() => setShowPasswordFields(!showPasswordFields)}
          disabled={isChangingPassword}
          className={`flex gap-2 items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            isChangingPassword
              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
              : showPasswordFields
              ? 'text-red-600 bg-red-50 hover:bg-red-100'
              : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
          }`}
        >
          <Edit3 size={16} />
          {isChangingPassword ? 'Changing...' : showPasswordFields ? 'Cancel' : 'Change Password'}
        </button>
      </div>

      {!showPasswordFields ? (
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex gap-3 items-center">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <Lock size={20} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Password Security</h3>
              <p className="text-sm text-gray-600">Your password is secure. Click "Change Password" to update it.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Security Notice */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-2 items-start">
              <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-800">Security Notice</p>
                <p className="mt-1 text-blue-700">
                  After changing your password, you will be automatically logged out and redirected to the login page.
                </p>
              </div>
            </div>
          </div>

          {/* Password Form */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-6">
              <PasswordInput
                label="Current Password"
                value={passwordData.oldPassword}
                onChange={(value) => handlePasswordChange('oldPassword', value)}
                placeholder="Enter your current password"
                disabled={isChangingPassword}
                showPassword={showPasswords.oldPassword}
                onToggleVisibility={() => togglePasswordVisibility('oldPassword')}
              />

              <PasswordInput
                label="New Password"
                value={passwordData.newPassword}
                onChange={(value) => handlePasswordChange('newPassword', value)}
                placeholder="Enter new password"
                disabled={isChangingPassword}
                showPassword={showPasswords.newPassword}
                onToggleVisibility={() => togglePasswordVisibility('newPassword')}
                showStrength={true}
              />

              <PasswordInput
                label="Confirm New Password"
                value={passwordData.confirmPassword}
                onChange={(value) => handlePasswordChange('confirmPassword', value)}
                placeholder="Confirm your new password"
                disabled={isChangingPassword}
                showPassword={showPasswords.confirmPassword}
                onToggleVisibility={() => togglePasswordVisibility('confirmPassword')}
              />
            </div>

            {/* Password Requirements */}
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="flex gap-2 items-center mb-3 font-medium text-gray-900">
                  <Shield size={16} className="text-emerald-600" />
                  Password Requirements
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex gap-2 items-center">
                    <div className={`w-2 h-2 rounded-full ${
                      passwordData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                    At least 8 characters long
                  </li>
                  <li className="flex gap-2 items-center">
                    <div className={`w-2 h-2 rounded-full ${
                      /[a-z]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                    Contains lowercase letter
                  </li>
                  <li className="flex gap-2 items-center">
                    <div className={`w-2 h-2 rounded-full ${
                      /[A-Z]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                    Contains uppercase letter
                  </li>
                  <li className="flex gap-2 items-center">
                    <div className={`w-2 h-2 rounded-full ${
                      /\d/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                    Contains number
                  </li>
                  <li className="flex gap-2 items-center">
                    <div className={`w-2 h-2 rounded-full ${
                      /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                    Contains special character
                  </li>
                  <li className="flex gap-2 items-center">
                    <div className={`w-2 h-2 rounded-full ${
                      passwordData.newPassword === passwordData.confirmPassword && passwordData.confirmPassword ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                    Passwords match
                  </li>
                </ul>
              </div>

              {/* Action Button */}
              <button 
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className={`flex items-center justify-center gap-2 px-6 py-3 w-full font-medium text-white rounded-lg transition-all duration-200 ${
                  isChangingPassword
                    ? 'bg-emerald-400 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-700 hover:shadow-lg'
                }`}
              >
                {isChangingPassword ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Update Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'security':
        return renderSecuritySection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="px-4 pb-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8 overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 shadow-sm">
          <div className="p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                  <User size={28} strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                    Account Settings
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 sm:text-base">
                    Manage your profile, documents, and security preferences.
                  </p>
                </div>
              </div>
              {user && (
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  <span className="inline-flex items-center rounded-lg bg-white/90 px-3 py-1.5 text-sm font-semibold text-emerald-700 shadow-sm ring-1 ring-emerald-200/60">
                    {user.agent_code}
                  </span>
                  {user.agent_type && (
                    <span className="inline-flex items-center rounded-lg bg-white/90 px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200/60">
                      {user.agent_type === 'rider'
                        ? 'Driver/Rider Agent'
                        : user.agent_type === 'general'
                        ? 'General Agent'
                        : user.agent_type === 'direct'
                        ? 'Direct Agent'
                        : user.agent_type}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold shadow-sm ring-1 ${
                      user.status === 1
                        ? 'bg-emerald-600 text-white ring-emerald-500/40'
                        : 'bg-red-100 text-red-700 ring-red-200/60'
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        user.status === 1 ? 'bg-emerald-200' : 'bg-red-400'
                      }`}
                    />
                    {user.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Left Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => !item.disabled && setActiveSection(item.id)}
                    disabled={item.disabled}
                    className={`w-full p-4 text-left rounded-xl transition-all duration-200 settings-nav-item ${
                      item.disabled
                        ? 'bg-gray-50 border border-gray-200 cursor-not-allowed opacity-60'
                        : isActive
                        ? 'bg-emerald-50 border border-emerald-200 shadow-sm active'
                        : 'bg-white border border-gray-200 hover:border-emerald-200 hover:bg-emerald-50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex gap-3 items-center">
                        <div className={`p-2 rounded-lg ${
                          item.disabled 
                            ? 'text-gray-400 bg-gray-100' 
                            : isActive 
                            ? 'text-emerald-600 bg-emerald-100' 
                            : 'text-gray-600 bg-gray-100'
                        }`}>
                          <Icon size={20} />
                        </div>
                        <div className="text-left">
                          <div className="flex gap-2 items-center">
                            <p className={`font-medium ${
                              item.disabled 
                                ? 'text-gray-500' 
                                : isActive 
                                ? 'text-emerald-700' 
                                : 'text-gray-900'
                            }`}>
                              {item.label}
                            </p>
                            {item.comingSoon && (
                              <span className="px-2 py-0.5 text-xs font-semibold text-white bg-orange-500 rounded-full shadow-sm whitespace-nowrap">
                                Coming Soon
                              </span>
                            )}
                          </div>
                          <p className={`text-xs ${
                            item.disabled 
                              ? 'text-gray-400' 
                              : isActive 
                              ? 'text-emerald-600' 
                              : 'text-gray-500'
                          }`}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <ArrowRight size={16} className={`${
                        item.disabled 
                          ? 'text-gray-300' 
                          : isActive 
                          ? 'text-emerald-600' 
                          : 'text-gray-400'
                      }`} />
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm settings-content">
              {renderContent()}
            </div>
          </div>
        </div>
    </div>
  );
}
